import * as React from 'react';

import { debounce } from 'ts-debounce';

import { Draggable, DragDropContext, Droppable } from 'react-beautiful-dnd';

import { IPhotoWithComments, IStory } from '../../Stores/StoryStore';
import { IUserPhoto } from '../../Stores/UserPhotoStore';

interface IProps {
    story: IStory;
    selectedId: number;
    photo: any;
    isReOrderingPhotos: boolean;
    location: any;
    tryUpdateRoutePhotoIDbyID(id: number, story: IStory): void;
    onReOrder(story: IStory, items: any): void;
}

const makeRows = (items: any, itemsPerRow: number) => {
    let i;
    let j;
    let row;
    let rows = [];

    for (i = 0, j = items.length; i < j; i += itemsPerRow) {
        row = items.slice(i, i + itemsPerRow);
        rows.push(row);
    }

    return rows;
};

const scrollToRef = (ref: any) => {
    if (ref && ref.current) {
        ref.current.scrollIntoView({
            behavior: 'smooth',
        });
    }
};

export function GridView(props: IProps) {

    const [itemsPerRow, setItemsPerRow] = React.useState(4);
    const [items, setItems] = React.useState<Array<any>>(props.story.photosWithComments);
    const [rows, setRows] = React.useState<Array<any>>([]);
    const [selectedItems, setSelectedItems] = React.useState<Array<any>>([]);
    const [selectedItemIndexes, setSelectedItemIndexes] = React.useState<String>('');
    const [isDragging, setIsDragging] = React.useState(false);
    const [isFirstView, setIsFirstView] = React.useState(true);
    const myRef = React.useRef(null);

    // Scroll to selected item when appropriate
    React.useEffect(() => {
        if (isFirstView && myRef.current) {
            scrollToRef(myRef);
            setIsFirstView(false);
        }
        if (props.location && props.location.state && props.location.state.scrollToSelected) {
            scrollToRef(myRef);
        }
    });

    // Update items per row on resize
    React.useLayoutEffect(() => {
        function updateSize() {
            let newItemsPerRow = 4;
            if (window.innerWidth > 1200) { newItemsPerRow = 5; }
            if (window.innerWidth > 1500) { newItemsPerRow = 6; }
            if (window.innerWidth > 1700) { newItemsPerRow = 7; }
            setItemsPerRow(newItemsPerRow);
            setRows(makeRows(items, newItemsPerRow));
        }
        const updateSizeDebounced = debounce(updateSize, 300);
        window.addEventListener('resize', updateSizeDebounced);
        updateSize();
        return () => window.removeEventListener('resize', updateSizeDebounced);
    }, []);

    const moveSelected = (source: any, destination: any, droppableSource: any, droppableDestination: any) => {
        let insertIndex = droppableDestination.index - 1;

        let insertBefore: IPhotoWithComments | null = null;
        let insertAtBeginning = false;
        if (insertIndex === -1) {
            insertBefore = destination[0];
        }

        let insertAfter = destination[0];
        if (insertIndex >= 0) {
            insertAfter = destination[insertIndex];
        }

        const originalItems = Array.from(items);
        let myItems = Array.from(items);

        // Remove the selected items
        myItems = myItems.filter((item) => {
            return !isSelected(item);
        });

        // Sort the selected items by original sort order
        selectedItems.sort(function(a, b) {
            return a.itemNumber - b.itemNumber;
        });

        let mergedInsertIndex = myItems.findIndex((item: any) => {
            return item.photo.id === insertAfter.photo.id;
        });

        if (insertBefore) {
            if (mergedInsertIndex === 0) {
                insertAtBeginning = true;
            }
            mergedInsertIndex = mergedInsertIndex - 1;
        }

        // If the user is trying to insert after a selected image, try to put it somewhere that makes sense
        if (mergedInsertIndex === -1 && !insertAtBeginning) {
            mergedInsertIndex = originalItems.findIndex((item: any) => {
                return item.photo.id === insertAfter.photo.id;
            });
            if (selectedItems.length > 1) {
                mergedInsertIndex --;
            }
        }

        if (insertAtBeginning) {
            myItems.unshift(...selectedItems);
        } else {
            myItems.splice(mergedInsertIndex + 1, 0, ...selectedItems);
        }

        setItems(myItems);
        setRows(makeRows(myItems, itemsPerRow));
        setSelectedItems([]);

        return myItems;

    };

    function onDragStart(result: any) {
        // If no items were selected, select the item the user is dragging
        if (selectedItems.length === 0) {
            let selected = items.find((item) => {
                return item.photo.id === parseInt(result.draggableId);
            });
            if (selected) {
                setSelectedItems([selected]);
            }
        }

        setIsDragging(true);
    }

    async function onDragEnd(result: any) {
        const { source, destination } = result;

        setIsDragging(false);

        // dropped outside the list
        if (!destination) {
            return;
        }

        const sourceId = +source.droppableId;
        const destinationID = +destination.droppableId;

        let reOrdered = moveSelected(rows[sourceId], rows[destinationID], source, destination);


        let i = 1;
        reOrdered.map((item) => {
            item.index = i++;
        });

        props.story.photosWithComments = reOrdered;

        await props.onReOrder(props.story, reOrdered);
        setRows(makeRows(reOrdered, itemsPerRow));

        setSelectedItemIndexes('');

    }

    function isSelected(photo: any) {
        let match = false;
        selectedItems.forEach(item => {
            if (item.photo.id === photo.photo.id) {
                match = true;
            }
        });
        return match;
    }

    function toggleSelected(photo: any) {
        let filtered = [];
        if (isSelected(photo)) {
            filtered = selectedItems.filter((value) => {
                return value.photo.id !== photo.photo.id;
            });
        } else {
            filtered = [...selectedItems, photo];
        }
        setSelectedItems(filtered);
        setSelectedItemIndexes(renderSelectedIndexes(filtered));
    }

    function renderSelectedIndexes(selectedItemsArray: Array<any>) {

        selectedItemsArray.sort(function(a, b) {
            return a.index - b.index;
        });

        let selectedIndexArray = selectedItemsArray.map((item) => {
            return item.index;
        });
        selectedIndexArray.slice(0, 10);

        return selectedIndexArray.join(', ');
    }

    function itemStyle() {
        return {
            width: `${100 / itemsPerRow}%`,
        };
    }

    return (
        <div className={'photoGrid customScrollBar customScrollBar--light customScrollBar--narrow'}>

            <DragDropContext onDragEnd={onDragEnd}  onDragStart={onDragStart}>

                {rows.map((row, index) => (

                    <Droppable key={index} droppableId={`${index}`} direction="horizontal">

                        {(provided, snapshot) => (

                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={`photoGrid__row photoGrid__row--${itemsPerRow}`}
                                key={index}
                            >

                                {row.map((img: any, imgIndex: number) => (

                                    <Draggable
                                        key={img.photo.id}
                                        isDragDisabled={!props.isReOrderingPhotos}
                                        draggableId={img.photo.id.toString()}
                                        index={imgIndex}
                                    >

                                        {(provided_img, snapshot_img) => (

                                            <div
                                                ref={provided_img.innerRef}
                                                {...provided_img.draggableProps}
                                                {...provided_img.dragHandleProps}
                                                className={'photoGrid__item'}
                                                onClick={() => {
                                                    if (!props.isReOrderingPhotos) {
                                                        props.tryUpdateRoutePhotoIDbyID(img.photo.id, props.story);
                                                    }
                                                }}
                                            >

                                                <span className={'photoGrid__item__label'}>
                                                   {snapshot_img.isDragging && selectedItemIndexes ? selectedItemIndexes : img.index}
                                                </span>

                                                {selectedItems.length > 1 && snapshot_img.isDragging && (
                                                    <div className="count">
                                                        {selectedItems.length}
                                                    </div>
                                                )}

                                                <div className="photoGrid__item__inner"
                                                     style={{opacity: (isSelected(img) && isDragging) ? 0.15 : 1}}
                                                >


                                                    <div ref={img.photo.id === props.selectedId ? myRef : null}
                                                        className={'photoGrid__image '  + ((img.photo.id === props.selectedId) ? 'active' : 'inactive')}>
                                                        <img src={img.photo.thumbnail_url || img.photo.url}/>
                                                    </div>

                                                    {props.isReOrderingPhotos && (

                                                        <label className="customCheckbox checkbox-container-round">
                                                            Select this Photo
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected(img)}
                                                                onChange={() => {
                                                                    toggleSelected(img);
                                                                }}
                                                            />
                                                            <span className="customCheckbox__check" />
                                                        </label>

                                                    )}

                                                </div>

                                            </div>

                                        )}

                                    </Draggable>

                                ))}

                                {provided.placeholder}

                            </div>

                        )}

                    </Droppable>

                ))}

            </DragDropContext>

        </div>
    );
}



// WEBPACK FOOTER //
// ./src/Pages/PhotoViewer/GridView.tsx